import { Flex, Heading, Text, } from "@chakra-ui/react";
import { useClient, useSettings } from "@/hooks";
import { useEffect, useState } from "react";
import { WithAuth } from "@/hoc";
import { NewContainer } from "@/layouts/SejaPago/NewContainer";
import { Input as ChakraInput, Select } from "@/components";
import { useForm } from "react-hook-form";
import { cleanDocument, clearMask, cpf_cnpj, detectDocumentType, formatCep, getNextLetter, getStatus, formatDateText, formatPhoneE164, isValidCnpj, isValidCpf, showToast } from "@/utils";
import isEmail from 'validator/lib/isEmail';
import { PhoneInput2 } from "@/components/Forms/PhoneInput2";
import { Button, Pills, Tooltip, Stepper, Loading, Alert } from "barte-design-system";
import { buildAddress, buildShareholder, ShareholderKind, toBrazilianPhone } from "./payload";
import { hasKycError, translateKycError } from "./kycErrors";
import { BiometriaStep } from "./BiometriaStep";

const needsBiometria = (status: string) => status === "KYC_APPROVED" || status === "BIOMETRIC_REJECTED";

const PjAccount = () => {
    const [owners, setOwners] = useState<string[]>(["A"]);
    const [naturalOwners, setNaturalOwners] = useState<string[]>([]);
    const [legalOwners, setLegalOwners] = useState<string[]>([]);
    const [nextLetter, setNextLetter] = useState<string>("B");
    const [status, setStatus] = useState("INACTIVE");
    const [kycAnalysisResult, setKycAnalysisResult] = useState<string[]>([]);
    const [isCnpjFound, setIsCnpjFound] = useState(false);
    const { getBankingAccount, postBankingAccount, putBankingAccount } = useSettings();
    const [businessAccountId, setBusinessAccountId] = useState<string | null>(null);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [isCepNotFound, setIsCepNotFound] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const { consultCNPJ, consultCep } = useClient();
    const [section, setSection] = useState(1);
    const user = JSON.parse(localStorage.getItem("barte-user-info"));
    const userDocument = localStorage.getItem("barte-user-document");
    const {
        register,
        formState: { errors },
        setValue,
        getValues,
        setError,
        clearErrors,
        watch,
    } = useForm();
    const societyTypes = [
        {
            title: "Sócio",
            id: 1,
            value: "PARTNER"
        },
        {
            title: "Procurador",
            id: 2,
            value: "PROXYHOLDER"
        },
        {
            title: "Representante Legal",
            id: 3,
            value: "LEGAL_REPRESENTATIVE"
        },
        {
            title: "Outros",
            id: 4,
            value: "OTHER"
        },
    ]

    const annualRevenueRanges = [
        { title: "Até R$ 50 mil", id: 1, value: "up_to_50k" },
        { title: "R$ 50 mil a R$ 500 mil", id: 2, value: "from_50k_to_500k" },
        { title: "R$ 500 mil a R$ 5 milhões", id: 3, value: "from_500k_to_5m" },
        { title: "R$ 5 milhões a R$ 50 milhões", id: 4, value: "from_5m_to_50m" },
        { title: "Acima de R$ 50 milhões", id: 5, value: "above_50m" },
    ]

    const monthlyRevenueRanges = [
        { title: "0 a 1 salário mínimo", id: 1, value: "0_to_1_sm" },
        { title: "1 a 2 salários mínimos", id: 2, value: "1_to_2_sm" },
        { title: "2 a 3 salários mínimos", id: 3, value: "2_to_3_sm" },
        { title: "3 a 5 salários mínimos", id: 4, value: "3_to_5_sm" },
        { title: "5 a 7 salários mínimos", id: 5, value: "5_to_7_sm" },
        { title: "7 a 10 salários mínimos", id: 6, value: "7_to_10_sm" },
        { title: "10 a 15 salários mínimos", id: 7, value: "10_to_15_sm" },
        { title: "15 a 20 salários mínimos", id: 8, value: "15_to_20_sm" },
        { title: "Acima de 20 salários mínimos", id: 9, value: "over_20_sm" },
    ]
    const businessType = [
        {
            title: "MEI",
            id: 1,
            value: "MEI"
        },
        {
            title: "ME",
            id: 2,
            value: "ME"
        },
        {
            title: "EI",
            id: 3,
            value: "EI"
        },
        {
            title: "EIRELI",
            id: 4,
            value: "EIRELI"
        },
        {
            title: "LTDA",
            id: 5,
            value: "LTDA"
        },
        {
            title: "SS",
            id: 6,
            value: "SS"
        },
        {
            title: "SA",
            id: 7,
            value: "SA"
        },
        {
            title: "Sem Fins Lucrativos",
            id: 8,
            value: "NON_PROFIT"
        },
    ]

    async function getInitialData() {
        try {
            setValue("businessType", "LTDA");
            setValue("ownerType-A", "PARTNER");
            setValue("companyDocument", cpf_cnpj(userDocument));
            setValue("companyName", user?.companyName);
            const response = await getBankingAccount();
            if (response?.data) {
                const { data } = response;
                const { account, status: accountStatus, kycAnalysisResult: kycResult, businessAccountId: accountId } = data;
                setStatus(accountStatus);
                const reasons = Array.isArray(kycResult)
                    ? kycResult
                        .map((reason) => (typeof reason === "string" ? reason : reason?.message))
                        .filter(Boolean)
                        .map(translateKycError)
                    : [];
                setKycAnalysisResult(reasons);
                if (accountId) {
                    setBusinessAccountId(accountId);
                }
                if (account) {
                    setValue("fantasyName", account.tradingName);
                    setValue("businessType", account.businessType);
                    setValue("mainActivity", account.mainActivity);
                    setValue("phone", account.phoneNumber);
                    setValue("openingDate", formatDateText(account.foundingDate));
                    setValue("mail", account.email);
                    setValue("companyName", account.legalName);
                    setValue("companyDocument", cpf_cnpj(account.document));
                    setValue("annualRevenueRange", account.annualRevenueRange);

                    if (account.address) {
                        setValue("zipCode", formatCep(account.address.postalCode));
                        setValue("street", account.address.street);
                        setValue("number", account.address.number);
                        setValue("complement", account.address.complement);
                        setValue("district", account.address.neighborhood);
                        setValue("city", account.address.city);
                        setValue("state", account.address.state);
                    }

                    if (account.shareholders?.length > 0) {
                        const labels = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
                        account.shareholders.forEach((shareholder: any, index: number) => {
                            const label = labels[index];
                            const cleanDoc = clearMask(shareholder.document);
                            const isCnpjOwner = cleanDoc.length > 11;
                            const documentType = isCnpjOwner ? "cnpj" : "cpf";

                            if (isCnpjOwner) {
                                legalOwners.push(label);
                            } else {
                                naturalOwners.push(label);
                            }

                            setValue(`ownerType-${label}`, shareholder.type || "PARTNER");
                            setValue(`ownerDocument-${label}`, cpf_cnpj(shareholder.document));
                            setValue(`ownerDocumentType-${label}`, documentType);
                            setValue(`ownerEmail-${label}`, shareholder.email);
                            setValue(`ownerPhone-${label}`, shareholder.phoneNumber);

                            if (!isCnpjOwner) {
                                setValue(`ownerFullName-${label}`, `${shareholder.firstName} ${shareholder.lastName}`);
                                setValue(`ownerBirthDate-${label}`, formatDateText(shareholder.birthDate));
                                setValue(`ownerMonthlyRevenue-${label}`, shareholder.monthlyRevenueRange);
                            } else {
                                setValue(`ownerLegalName-${label}`, shareholder.legalName);
                                setValue(`ownerFoundingDate-${label}`, formatDateText(shareholder.openingDate));
                                setValue(`ownerAnnualRevenue-${label}`, shareholder.annualRevenueRange);
                            }

                            if (shareholder.address) {
                                setValue(`ownerZipCode-${label}`, formatCep(shareholder.address.postalCode));
                                setValue(`ownerStreet-${label}`, shareholder.address.street);
                                setValue(`ownerState-${label}`, shareholder.address.state);
                                setValue(`ownerNumber-${label}`, shareholder.address.number);
                                setValue(`ownerCity-${label}`, shareholder.address.city);
                                setValue(`ownerCountry-${label}`, shareholder.address.country);
                                setValue(`ownerComplement-${label}`, shareholder.address.complement);
                                setValue(`ownerDistrict-${label}`, shareholder.address.neighborhood);
                            }

                            if (index > 0) {
                                setOwners((prev) => [...prev, label]);
                            }
                            setNextLetter(getNextLetter(label));
                        });
                    }
                }
            }
        } catch (error) {
            try {
                const { data } = await consultCNPJ(userDocument);
                setIsCnpjFound(true);
                setValue("companyDocument", cpf_cnpj(userDocument));
                setValue("companyName", data.companyName);
                if (data.address) {
                    setValue("zipCode", formatCep(data.address.zipCode));
                    setValue("street", data.address.street);
                    setValue("number", data.address.number);
                    setValue("complement", data.address.complement);
                    setValue("district", data.address.district);
                    setValue("city", data.address.city);
                    setValue("state", data.address.state);
                }
            } catch (cnpjError) {
                setIsCnpjFound(false);
                setValue("companyDocument", cpf_cnpj(userDocument));
                showToast({
                    type: "error",
                    message: "Erro ao consultar CNPJ. Por favor, preencha os dados manualmente.",
                });
            }
        } finally {
            setIsPageLoading(false);
        }
    }
    const submitForm = async () => {
        const values = getValues();
        setIsSubmitLoading(true);
        const shareholders = [
            ...naturalOwners
                .filter(no => !!clearMask(values?.[`ownerDocument-${no}`] ?? ""))
                .map(no => buildShareholder(no, ShareholderKind.NATURAL, values)),
            ...legalOwners
                .filter(lo => !!clearMask(values?.[`ownerDocument-${lo}`] ?? ""))
                .map(lo => buildShareholder(lo, ShareholderKind.LEGAL, values)),
        ]
        const address = buildAddress({
            street: values?.street,
            number: values?.number,
            complement: values?.complement,
            neighborhood: values?.district,
            zipCode: values?.zipCode,
            city: values?.city,
            state: values?.state,
        })
        const phoneNumber = toBrazilianPhone(values?.phone ?? values?.[`ownerPhone-A`])
        try {
            if (businessAccountId) {
                const updatePayload = {
                    legalName: values?.companyName,
                    email: values?.mail,
                    phoneNumber,
                    annualRevenueRange: values?.annualRevenueRange,
                    shareholders,
                    address,
                }
                await putBankingAccount(updatePayload);
            } else {
                const createPayload = {
                    externalId: `externalId-${cleanDocument(values?.companyDocument)}`,
                    document: cleanDocument(values?.companyDocument),
                    legalName: values?.companyName,
                    email: values?.mail,
                    phoneNumber,
                    annualRevenueRange: values?.annualRevenueRange,
                    shareholders,
                    address,
                }
                const response = await postBankingAccount(createPayload);
                const newAccountId = response?.data?.id ?? response?.data?.businessAccountId;
                if (newAccountId) {
                    setBusinessAccountId(newAccountId);
                }
            }
            setStatus("PENDING");
            showToast({
                type: "success",
                message: "Conta enviada com sucesso. Aguarde o processamento da mesma.",
            });
        } catch (error) {
            error?.response?.data?.errors.map((error) => {
                showToast({
                    type: "error",
                    message: `${error?.code} - ${error?.description}`
                })
            });
        } finally {
            setIsSubmitLoading(false);
        }
    }
    const updateOwners = async () => {
        const updateOwners = [...owners];
        updateOwners.push(nextLetter);
        setOwners(updateOwners);
        setValue(`ownerType-${nextLetter}`, "PARTNER");
        setNextLetter(getNextLetter(nextLetter));
    }
    const validateForm = () => {
        if (status !== "INACTIVE" && status !== "FAILED" && status !== "PENDING") {
            if (section < 3) {
                setSection(section + 1);
                return
            }
        }
        const sectionOneFields = ["companyDocument", "companyName", "mail", "annualRevenueRange", "zipCode", "street", "number", "district", "city", "state"];
        const sectionTwoFields: string[] = [];
        const sectionThreeFieldsNaturalOwner = ["ownerEmail", "ownerPhone", "ownerDocument", "ownerFullName", "ownerType", "ownerBirthDate", "ownerMonthlyRevenue", "ownerZipCode", "ownerStreet", "ownerNumber", "ownerDistrict", "ownerCity", "ownerState"];
        const sectionThreeFieldsLegalOwner = ["ownerEmail", "ownerPhone", "ownerDocument", "ownerLegalName", "ownerType", "ownerAnnualRevenue", "ownerZipCode", "ownerStreet", "ownerNumber", "ownerDistrict", "ownerCity", "ownerState"];
        let errorsSectionOne = [];
        let errorsSectionTwo = [];
        let errorsSectionTree = [];
        if (section === 1) {
            sectionOneFields.map(field => {
                const state = getValues(field);
                if (state === undefined || state === "") {
                    errorsSectionOne.push({ field: field, message: "Preenchimento obrigatório!" });
                    return
                }
                if (field === "mail" && !isEmail(state)) {
                    errorsSectionOne.push({ field: field, message: "E-mail inválido!" });
                    return
                }
            })
            if (errorsSectionOne?.length > 0) {
                errorsSectionOne.map(field => {
                    setError(field.field, { type: "custom", message: field.message });
                })
                return
            }
            setSection(2);
            errorsSectionOne = [];
            clearErrors();
            return
        }
        if (section === 2) {
            sectionTwoFields.map(field => {
                const state = getValues(field);
                if (state === undefined || state === "") {
                    errorsSectionTwo.push({ field: field, message: "Preenchimento obrigatório!" });
                    return
                }
            })
            if (errorsSectionTwo?.length > 0) {
                errorsSectionTwo.map(field => {
                    setError(field.field, { type: "custom", message: field.message });
                })
                return
            }
            setSection(3);
            errorsSectionTwo = [];
            clearErrors();
            return
        }
        if (section === 3) {
            owners.forEach(no => {
                if (legalOwners.includes(no)) {
                    sectionThreeFieldsLegalOwner.map(field => {
                        const state = getValues(`${field}-${no}`);
                        if (state === undefined || state === "") {
                            errorsSectionTree.push(`${field}-${no}`);
                        }
                    })
                }
                if (naturalOwners.includes(no)) {
                    sectionThreeFieldsNaturalOwner.map(field => {
                        const state = getValues(`${field}-${no}`);
                        if (state === undefined || state === "") {
                            errorsSectionTree.push(`${field}-${no}`);
                        }
                    })
                }
            })
            if (errorsSectionTree?.length > 0) {
                errorsSectionTree.map(field => {
                    setError(field, { type: "custom", message: "Preenchimento obrigatório!" });
                })
                return
            }
            errorsSectionTree = [];
            clearErrors();
            submitForm();
            return

        }
    }
    async function handleValidateCnpjCpf(owner) {
        const updateNaturalOwners = [...naturalOwners];
        const updateLegalOwners = [...legalOwners];
        clearErrors(`ownerDocument-${owner}`);
        if (detectDocumentType(watch(`ownerDocument-${owner}`)) !== "CNPJ") {
            if (!isValidCpf(watch(`ownerDocument-${owner}`))) {
                setError(`ownerDocument-${owner}`, { type: "custom", message: "Cpf inválido." });
            }
            setValue(`ownerDocumentType-${owner}`, "cpf");
            if (updateNaturalOwners.includes(owner)) return;
            if (updateLegalOwners.includes(owner)) {
                const filteredLegalOwners = updateLegalOwners.filter(ulo => ulo !== owner);
                setLegalOwners(filteredLegalOwners);
            }
            updateNaturalOwners.push(owner);
            setNaturalOwners(updateNaturalOwners);
            return
        }
        if (!isValidCnpj(watch(`ownerDocument-${owner}`))) {
            setError(`ownerDocument-${owner}`, { type: "custom", message: "Cnpj inválido." });
            return
        }
        if (updateLegalOwners.includes(owner)) return;
        if (updateNaturalOwners.includes(owner)) {
            const filteredNaturalOwners = updateNaturalOwners.filter(ulo => ulo !== owner);
            setNaturalOwners(filteredNaturalOwners);
        }
        updateLegalOwners.push(owner);
        setLegalOwners(updateLegalOwners);
        setValue(`ownerDocumentType-${owner}`, "cnpj");
    }
    async function handleGetCep(owner?: string) {
        if (owner) {
            try {
                clearErrors(`ownerZipCode-${owner}`)
                setIsCepLoading(true);
                const { data } = await consultCep(clearMask(watch(`ownerZipCode-${owner}`)));
                setValue(`ownerStreet-${owner}`, data.logradouro);
                setValue(`ownerDistrict-${owner}`, data.bairro);
                setValue(`ownerCity-${owner}`, data.localidade);
                setValue(`ownerState-${owner}`, data.uf);
                setValue(`ownerComplement-${owner}`, data.complemento);
                setValue(`ownerContry-${owner}`, "BR");
                setIsCepNotFound(false);
            } catch {
                setIsCepNotFound(true);
            } finally {
                setIsCepLoading(false);
            }
            return
        }
        try {
            clearErrors("zipCode")
            setIsCepLoading(true);
            const { data } = await consultCep(clearMask(watch("zipCode")));
            setValue("street", data.logradouro);
            setValue("district", data.bairro);
            setValue("city", data.localidade);
            setValue("state", data.uf);
            setValue("complement", data.complemento);
            setValue("country", "BR");
            setIsCepNotFound(false);
        } catch {
            setIsCepNotFound(true);
        } finally {
            setIsCepLoading(false);
        }
    }
    function removeOwner(label: string) {
        const updatedOwners = owners.filter(o => o !== label);
        const updatedNatural = naturalOwners.filter(o => o !== label);
        const updatedLegal = legalOwners.filter(o => o !== label);
        const combined = updatedOwners.filter(o => o !== "A");
        const newOwners = ["A", ...combined.map((_, idx) => String.fromCharCode(66 + idx))];
        const reMap = Object.fromEntries(combined.map((oldLabel, idx) => [oldLabel, String.fromCharCode(66 + idx)]));
        const newNaturalOwners = updatedNatural.map(o => reMap[o] || o);
        const newLegalOwners = updatedLegal.map(o => reMap[o] || o);
        const fieldsToUpdate = [
            "ownerType", "ownerDocument", "ownerDocumentType", "ownerFullName", "ownerBirthDate",
            "ownerMonthlyRevenue", "ownerLegalName", "ownerFoundingDate", "ownerAnnualRevenue", "ownerEmail", "ownerPhone",
            "ownerZipCode", "ownerStreet", "ownerState", "ownerNumber",
            "ownerCity", "ownerCountry", "ownerComplement", "ownerDistrict"
        ];
        const currentValues = getValues();
        for (const [oldLabel, newLabel] of Object.entries(reMap)) {
            fieldsToUpdate.forEach((field) => {
                const oldKey = `${field}-${oldLabel}`;
                const newKey = `${field}-${newLabel}`;
                if (currentValues[oldKey] !== undefined) {
                    setValue(newKey, currentValues[oldKey]);
                }
                setValue(oldKey, null);
            });
        }
        fieldsToUpdate.forEach((field) => {
            setValue(`${field}-${label}`, null);
        });
        setOwners(newOwners);
        setNaturalOwners(newNaturalOwners);
        setLegalOwners(newLegalOwners);
        setNextLetter(getNextLetter(newOwners[newOwners.length - 1]));
    }
    useEffect(() => {
        getInitialData()
    }, [user.document])

    const showKycErrors = hasKycError(status, kycAnalysisResult);

    return (
        <>
            {isPageLoading ? (
                <Loading type="page" description="Carregando dados da conta..." />
            ) : needsBiometria(status) && businessAccountId ? (
                <BiometriaStep
                    accountId={businessAccountId}
                    onAllApproved={getInitialData}
                />
            ) : (
                <Flex
                    w="100%"
                    flexDir="column"
                    padding="2rem 6rem"
                    gap="1rem"
                >
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Flex
                            gap="8px"
                        >
                            <Heading fontSize="1.5rem">Ativar Conta PJ</Heading>
                            {showKycErrors ? (
                                <Tooltip label={kycAnalysisResult.join("\n")}>
                                    <Pills label={getStatus(status)} variant="light" state="warning" size="base" />
                                </Tooltip>
                            ) : (
                                <Pills label={getStatus(status)} variant="light" state="warning" size="base" />
                            )}
                        </Flex>

                        <Flex
                            padding="10px 16px 10px 16px"
                            backgroundColor="#E6F7FF"
                        >
                            <Text
                                color="#003A8C"
                                fontSize=".8rem"
                            >
                                Ative sua conta PJ e mantenha sua conta da Barte fucionando
                            </Text>
                        </Flex>

                    </Flex>
                    {showKycErrors && (
                        <Alert
                            variant="error"
                            title="Não foi possível validar sua conta"
                            description={kycAnalysisResult.join(" • ")}
                            showCloseButton={false}
                        />
                    )}
                    <Stepper
                        steps={[
                            { label: "Dados da empresa" },
                            { label: "Dados adicionais" },
                            { label: "Dados dos socios" },
                        ]}
                        currentStep={status !== "INACTIVE" && status !== "FAILED" ? 4 : section}
                    />
                    {section === 1 && (
                        <>
                            <Flex
                                border="1px solid #EDF1F7"
                            >
                                <NewContainer
                                    type="check"
                                    w="100%"
                                    title="Informações gerais"
                                    startsOpen
                                    p="25px"
                                >
                                    <ChakraInput
                                        label="CNPJ"
                                        type="text"
                                        placeholder="00.X01.000/0001-10"
                                        name="document"
                                        register={{ ...register("companyDocument") }}
                                        errors={errors?.companyDocument}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <ChakraInput
                                        label="Razão social"
                                        type="text"
                                        placeholder=""
                                        name="companyName"
                                        register={{ ...register("companyName") }}
                                        errors={errors?.companyName}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <ChakraInput
                                        label="Data de abertura"
                                        type="text"
                                        placeholder="00/00/0000"
                                        name="openingDate"
                                        register={{ ...register("openingDate") }}
                                        errors={errors?.openingDate}
                                        mask="99/99/9999"
                                        max={10}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <ChakraInput
                                        label="E-mail"
                                        type="text"
                                        id="mail"
                                        name="mail"
                                        register={{ ...register("mail") }}
                                        errors={errors?.mail}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <PhoneInput2
                                        type="text"
                                        name="phone"
                                        label="Telefone"
                                        valueData={formatPhoneE164(watch("phone"))}
                                        setValue={(value) => setValue("phone", value)}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <Select
                                        w="100%"
                                        name="annualRevenueRange"
                                        label="Faturamento anual estimado"
                                        defaultValue={watch("annualRevenueRange") || ""}
                                        onChange={(e) => setValue("annualRevenueRange", e.target.value)}
                                        optionsList={annualRevenueRanges}
                                        errors={errors?.annualRevenueRange}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                </NewContainer>
                            </Flex>
                            <Flex
                                border="1px solid #EDF1F7"
                            >
                                <NewContainer
                                    type="check"
                                    w="100%"
                                    title="Endereço da empresa"
                                    startsOpen
                                    p="25px"
                                >
                                    <ChakraInput
                                        label="CEP"
                                        type="text"
                                        placeholder="23000-X0"
                                        name="zipCode"
                                        register={{
                                            ...register("zipCode", {
                                                onBlur: () => handleGetCep(),
                                            })
                                        }}
                                        errors={errors?.zipCode}
                                        mask="99999-999"
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <ChakraInput
                                        label="Rua"
                                        type="text"
                                        placeholder=""
                                        name="street"
                                        register={{ ...register("street") }}
                                        errors={errors?.street}
                                        isDisabled={isCepLoading || status === "ACTIVE"}
                                    />
                                    <Flex
                                        gap="1rem"
                                    >
                                        <ChakraInput
                                            label="Número"
                                            type="text"
                                            name="number"
                                            register={{ ...register("number") }}
                                            errors={errors?.number}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        <ChakraInput
                                            label="Complemento"
                                            type="text"
                                            name="complement"
                                            register={{ ...register("complement") }}
                                            errors={errors?.complement}
                                            isDisabled={isCepLoading || status === "ACTIVE"}
                                        />
                                    </Flex>
                                    <ChakraInput
                                        label="Bairro"
                                        type="text"
                                        name="district"
                                        register={{ ...register("district") }}
                                        errors={errors?.district}
                                        isDisabled={isCepLoading || status === "ACTIVE"}
                                    />
                                    <Flex
                                        gap="1rem"
                                    >
                                        <ChakraInput
                                            label="Cidade"
                                            type="text"
                                            name="city"
                                            register={{ ...register("city") }}
                                            errors={errors?.city}
                                            isDisabled={isCepLoading || status === "ACTIVE"}
                                        />
                                        <ChakraInput
                                            label="Estado"
                                            type="text"
                                            name="state"
                                            register={{ ...register("state") }}
                                            errors={errors?.state}
                                            isDisabled={isCepLoading || status === "ACTIVE"}
                                        />
                                    </Flex>
                                </NewContainer>
                            </Flex>
                        </>
                    )}
                    {section === 2 && (
                        <>
                            <Flex
                                border="1px solid #EDF1F7"
                            >
                                <NewContainer
                                    type="check"
                                    w="100%"
                                    title="Informações gerais"
                                    startsOpen
                                    p="25px"
                                >
                                    <ChakraInput
                                        label="Nome fantasia"
                                        type="text"
                                        name="fantasyName"
                                        register={{ ...register("fantasyName") }}
                                        errors={errors?.fantasyName}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <Select
                                        w="100%"
                                        name="businessType"
                                        label="Categoria de empresa"
                                        defaultValue="LTDA"
                                        onChange={(e) => setValue("businessType", e.target.value)}
                                        optionsList={businessType}
                                        isDisabled={status === "ACTIVE"}
                                    />
                                    <ChakraInput
                                        label="CNAE"
                                        type="text"
                                        name="mainActivity"
                                        register={{ ...register("mainActivity") }}
                                        errors={errors?.mainActivity}
                                        isDisabled={status === "ACTIVE"}
                                        mask="999999999"
                                    />
                                </NewContainer>
                            </Flex>
                        </>
                    )}
                    {section === 3 && (
                        <>
                            {owners.map(no => (
                                <Flex
                                    border="1px solid #EDF1F7"
                                >
                                    <NewContainer
                                        key={no}
                                        type="check"
                                        w="100%"
                                        title={`Sócio ${no}`}
                                        startsOpen={no === "A"}
                                        p="25px"
                                    >
                                        <ChakraInput
                                            label="Documento"
                                            type="text"
                                            placeholder={no === "A" ? "CPF" : "CNPJ/CPF"}
                                            mask="cpf/cnpj"
                                            maxLength={no === "A" ? 14 : 18}
                                            name={`ownerDocument-${no}`}
                                            register={{
                                                ...register(`ownerDocument-${no}`, {
                                                    onBlur: () => handleValidateCnpjCpf(no),
                                                })
                                            }}
                                            errors={errors?.[`ownerDocument-${no}`]}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        {watch(`ownerDocumentType-${no}`) === "cnpj" && (
                                            <ChakraInput
                                                label="Nome"
                                                type="text"
                                                placeholder="Razão Social"
                                                name={`ownerLegalName-${no}`}
                                                register={{ ...register(`ownerLegalName-${no}`) }}
                                                errors={errors?.[`ownerLegalName-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                            />
                                        )}
                                        {watch(`ownerDocumentType-${no}`) === "cnpj" && (
                                            <Select
                                                w="100%"
                                                name={`ownerAnnualRevenue-${no}`}
                                                label="Faturamento anual"
                                                defaultValue={watch(`ownerAnnualRevenue-${no}`) || ""}
                                                onChange={(e) => setValue(`ownerAnnualRevenue-${no}`, e.target.value)}
                                                optionsList={annualRevenueRanges}
                                                errors={errors?.[`ownerAnnualRevenue-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                            />
                                        )}
                                        {watch(`ownerDocumentType-${no}`) === "cpf" && (
                                            <ChakraInput
                                                label="Nome completo"
                                                type="text"
                                                placeholder="João da Silva Santos"
                                                name={`ownerFullName-${no}`}
                                                register={{ ...register(`ownerFullName-${no}`) }}
                                                errors={errors?.[`ownerFullName-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                                tooltipText="Não use abreviações, como J.S.S ou J.S.S.S"
                                            />
                                        )}
                                        <Select
                                            w="100%"
                                            name={`ownerType-${no}`}
                                            label="Tipo de sociedade"
                                            defaultValue="PARTNER"
                                            onChange={(e) => setValue(`ownerType-${no}`, e.target.value)}
                                            optionsList={societyTypes}
                                            errors={errors?.[`ownerType-${no}`]}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        {watch(`ownerDocumentType-${no}`) === "cpf" && (
                                            <ChakraInput
                                                label="Data de nascimento"
                                                placeholder="dd/mm/aaaa"
                                                mask="99/99/9999"
                                                type="text"
                                                name={`ownerBirthDate-${no}`}
                                                register={{ ...register(`ownerBirthDate-${no}`) }}
                                                errors={errors?.[`ownerBirthDate-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                            />
                                        )}
                                        {watch(`ownerDocumentType-${no}`) === "cpf" && (
                                            <Select
                                                w="100%"
                                                name={`ownerMonthlyRevenue-${no}`}
                                                label="Faturamento mensal"
                                                defaultValue={watch(`ownerMonthlyRevenue-${no}`) || ""}
                                                onChange={(e) => setValue(`ownerMonthlyRevenue-${no}`, e.target.value)}
                                                optionsList={monthlyRevenueRanges}
                                                errors={errors?.[`ownerMonthlyRevenue-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                            />
                                        )}
                                        {watch(`ownerDocumentType-${no}`) === "cnpj" && (
                                            <ChakraInput
                                                label="Data de abertura"
                                                placeholder="dd/mm/aaaa"
                                                mask="99/99/9999"
                                                type="text"
                                                name={`ownerFoundingDate-${no}`}
                                                register={{ ...register(`ownerFoundingDate-${no}`) }}
                                                errors={errors?.[`ownerFoundingDate-${no}`]}
                                                isDisabled={status === "ACTIVE"}
                                            />
                                        )}

                                        <ChakraInput
                                            label="Email"
                                            type="text"
                                            placeholder="Seu email"
                                            name={`ownerEmail-${no}`}
                                            register={{ ...register(`ownerEmail-${no}`) }}
                                            errors={errors?.[`ownerEmail-${no}`]}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        <PhoneInput2
                                            type="text"
                                            name="phone"
                                            label="Telefone"
                                            valueData={formatPhoneE164(watch(`ownerPhone-${no}`))}
                                            setValue={(value) => setValue(`ownerPhone-${no}`, value)}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        <ChakraInput
                                            label="CEP"
                                            type="text"
                                            placeholder="00000-X00"
                                            name={`ownerZipCode-${no}`}
                                            mask="99999-999"
                                            register={{
                                                ...register(`ownerZipCode-${no}`, {
                                                    onBlur: () => handleGetCep(no)
                                                })
                                            }}
                                            errors={errors?.[`ownerZipCode-${no}`]}
                                            isDisabled={status === "ACTIVE"}
                                        />
                                        <ChakraInput
                                            label="Rua"
                                            type="text"
                                            placeholder=""
                                            name={`ownerStreet-${no}`}
                                            register={{ ...register(`ownerStreet-${no}`) }}
                                            errors={errors?.[`ownerStreet-${no}`]}
                                            isDisabled={isCepLoading || status === "ACTIVE"}
                                        />
                                        <Flex
                                            gap="1rem"
                                        >
                                            <ChakraInput
                                                label="Número"
                                                type="text"
                                                name={`ownerNumber-${no}`}
                                                register={{ ...register(`ownerNumber-${no}`) }}
                                                errors={errors?.[`ownerNumber-${no}`]}
                                                isDisabled={status !== "INACTIVE" && status === "ACTIVE"}
                                            />
                                            <ChakraInput
                                                label="Complemento"
                                                type="text"
                                                name={`ownerComplement-${no}`}
                                                register={{ ...register(`ownerComplement-${no}`) }}
                                                errors={errors?.[`ownerComplement-${no}`]}
                                                isDisabled={isCepLoading || status === "ACTIVE"}
                                            />
                                        </Flex>
                                        <ChakraInput
                                            label="Bairro"
                                            type="text"
                                            name={`ownerDistrict-${no}`}
                                            register={{ ...register(`ownerDistrict-${no}`) }}
                                            errors={errors?.[`ownerDistrict-${no}`]}
                                            isDisabled={isCepLoading || status === "ACTIVE"}
                                        />
                                        <Flex
                                            gap="1rem"
                                        >
                                            <ChakraInput
                                                label="Cidade"
                                                type="text"
                                                name={`ownerCity-${no}`}
                                                register={{ ...register(`ownerCity-${no}`) }}
                                                errors={errors?.[`ownerCity-${no}`]}
                                                isDisabled={!isCepNotFound}
                                            />
                                            <ChakraInput
                                                label="Estado"
                                                type="text"
                                                name={`ownerState-${no}`}
                                                register={{ ...register(`ownerState-${no}`) }}
                                                errors={errors?.[`ownerState-${no}`]}
                                                isDisabled={!isCepNotFound}
                                            />
                                        </Flex>
                                        {no !== "A" && (
                                            <Button
                                                variant="outline"
                                                onClick={() => removeOwner(no)}
                                                style={{ maxWidth: "150px" }}
                                            >
                                                Remover sócio
                                            </Button>
                                        )}
                                    </NewContainer>
                                </Flex>
                            ))}

                            <NewContainer
                                type="check"
                                w="100%"
                                title={`Sócio ${nextLetter}`}
                                p="25px"
                                startsOpen={false}
                                button
                                buttonAction={updateOwners}
                                buttonTitle="+ Adicionar sócio"
                                buttonDisabled={false}
                            >

                            </NewContainer>
                        </>
                    )}
                    <Flex
                        marginLeft="auto"
                        gap="8px"
                    >
                        {section > 1 && (
                            <Button
                                variant="outline"
                                onClick={() => setSection(section - 1)}
                            >
                                Voltar
                            </Button>
                        )}
                        {section < 3 && (
                            <Button
                                onClick={() => {
                                    validateForm()
                                }}
                            >
                                Próximo
                            </Button>
                        )}
                        {section === 3 && (
                            <Button
                                onClick={() => {
                                    validateForm()
                                }}
                                loading={isSubmitLoading}
                                disabled={isSubmitLoading || (status === "ACTIVE" && section === 3)}
                            >
                                Finalizar
                            </Button>
                        )}
                    </Flex>
                </Flex>
            )}
        </>
    );
};

export default WithAuth(PjAccount);
